import React, { FC, useState } from 'react';
import { 
  Key, 
  Lock, 
  User, 
  Eye, 
  EyeOff, 
  Video,
  ShieldCheck,
  AlertTriangle,
  Sparkles
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Employee } from '../types';

interface CreativeLoginPageProps {
  employees: Employee[];
  onLoginSuccess: (empId: string) => void;
}

export const CreativeLoginPage: FC<CreativeLoginPageProps> = ({ employees, onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isSuccessating, setIsSuccessating] = useState(false);
  const [loadingStep, setLoadingStep] = useState('Yoxlanılır...');

  // Password structural evaluation (safety level check)
  const getPasswordStrength = () => {
    if (!password) return { label: 'Boşdur', color: 'bg-zinc-800 text-zinc-500', width: 'w-0' };
    if (password === '1234' || password === 'admin') {
      return { label: 'Etibarsız (Şablon)', color: 'bg-rose-500/20 text-rose-400 border border-rose-500/30', width: 'w-1/4' };
    }
    const hasLetters = /[a-zA-Z]/.test(password);
    const hasNumbers = /[0-9]/.test(password);
    const hasSymbols = /[^a-zA-Z0-9]/.test(password);
    
    if (password.length >= 8 && hasLetters && hasNumbers && hasSymbols) {
      return { label: 'Yüksək qorunma dərəcəsi', color: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30', width: 'w-full' };
    }
    if (password.length >= 6 && hasLetters && hasNumbers) {
      return { label: 'Orta qorunma dərəcəsi', color: 'bg-amber-500/20 text-amber-400 border border-amber-500/30', width: 'w-2/3' };
    }
    return { label: 'Zəif Şifrə', color: 'bg-rose-500/20 text-rose-400 border border-rose-500/30', width: 'w-1/3' };
  };

  const handleAdminBypass = () => {
    setIsSuccessating(true);
    setLoadingStep('Sürətli giriş təsdiqlənir (Status: Administrator)...');
    setTimeout(() => {
      setLoadingStep('İdarəetmə paneli yüklənir...');
      setTimeout(() => {
        onLoginSuccess('admin');
      }, 500);
    }, 600);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setIsSuccessating(false);

    if (!username.trim()) {
      setErrorMsg('Zəhmət olmasa istifadəçi adınızı, e-mail ünvanınızı və ya ID yazın.');
      return;
    }
    if (!password.trim()) {
      setErrorMsg('Zəhmət olmasa şifrənizi daxil edin.');
      return;
    }

    setIsSuccessating(true);
    setLoadingStep('Serverə qoşulur...');

    try {
      const res = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login: username.trim(), password: password.trim() }),
      });

      const data = await res.json();

      if (res.ok && data.token) {
        localStorage.setItem('crm_token', data.token);
        setLoadingStep(`Xoş gəldiniz, ${data.employee.name}...`);
        setTimeout(() => {
          onLoginSuccess(data.employee.id);
        }, 600);
      } else {
        setIsSuccessating(false);
        setErrorMsg(data.message || 'Giriş rədd edildi.');
      }
    } catch (err: any) {
      setIsSuccessating(false);
      console.error('Login fetch error:', err);
      if (err instanceof TypeError && err.message === 'Failed to fetch') {
        setErrorMsg('Serverə qoşulmaq mümkün olmadı. İnternet bağlantınızı yoxlayın və ya CORS problemi ola bilər.');
      } else {
        setErrorMsg('Serverə qoşulmaq mümkün olmadı. Zəhmət olmasa yenidən cəhd edin.');
      }
    }
  };

  const strength = getPasswordStrength();

  return (
    <div className="min-h-screen w-full bg-[#05070c] text-zinc-100 flex items-center justify-center relative p-4 overflow-hidden font-sans" id="creative_login_page_container">
      {/* Decorative dark vector radial ambient effect */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-indigo-950/20 via-[#05070c] to-[#040508] pointer-events-none z-0" />
      
      {/* Decorative colored glow rings */}
      <div className="absolute -top-32 -left-32 w-80 h-80 bg-indigo-600/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-80 h-80 bg-violet-600/10 rounded-full blur-3xl pointer-events-none" />

      {/* Main vault-like centralized card container */}
      <div className="w-full max-w-md relative z-10" id="login_card_assembly">
        <div className="bg-zinc-950/80 backdrop-blur-xl border border-zinc-800/80 rounded-[28px] shadow-2xl p-8 relative overflow-hidden" id="login_terminal_window">
          
          {/* Subtle laser indicator border line for visual premium finish */}
          <div className="absolute top-0 left-12 right-12 h-[1px] bg-gradient-to-r from-transparent via-indigo-500 to-transparent" />
          
          <div className="flex flex-col items-center text-center space-y-4 mb-8">
            {/* Smooth glowing video lens visual */}
            <div className="relative flex items-center justify-center">
              <div className="absolute inset-0 bg-indigo-500/20 rounded-full blur-md animate-pulse" />
              <div className="w-14 h-14 bg-zinc-900 border border-zinc-700/80 rounded-2xl flex items-center justify-center text-indigo-400 shadow-inner relative z-10">
                <Video className="w-6 h-6" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-center gap-1.5">
                <h1 className="text-xl font-extrabold text-white tracking-tight font-mono">QR Menu HQ</h1>
                <span className="bg-indigo-500/10 text-indigo-400 font-mono text-[9px] font-bold px-1.5 py-0.5 rounded border border-indigo-500/20">
                  CRM
                </span>
              </div>
              <p className="text-xs text-zinc-400 mt-1.5 max-w-[280px] mx-auto leading-normal">
                Restoran rəqəmsallaşdırılması, dəstək biletləri və sistem mərkəzi idarəetmə terminalı
              </p>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {isSuccessating ? (
              /* PROGRESS LOADER TRANSITION */
              <motion.div
                key="loader"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                className="py-12 flex flex-col items-center justify-center space-y-5"
                id="interactive_success_loader"
              >
                <div className="relative">
                  <div className="w-12 h-12 rounded-full border-2 border-indigo-500/20 border-t-indigo-400 animate-spin" />
                  <ShieldCheck className="w-5 h-5 text-indigo-400 absolute inset-0 m-auto animate-bounce" />
                </div>
                <div className="space-y-1.5 text-center">
                  <p className="text-xs font-mono font-bold text-indigo-400 tracking-wide">GİRİŞ TƏSDİQLƏNDİ</p>
                  <p className="text-xs text-zinc-300 animate-pulse font-medium">{loadingStep}</p>
                </div>
              </motion.div>
            ) : (
              /* SECURED LOGIN FORM */
              <motion.form
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onSubmit={handleLogin}
                className="space-y-5"
                id="secured_login_form_element"
              >
                {/* 1. Username text input field */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label htmlFor="auth_login_input" className="text-[10px] font-mono font-bold text-zinc-500 uppercase tracking-wider">
                      İstifadəçi adı / E-mail
                    </label>
                    <span className="text-[9px] text-zinc-650 font-mono select-none">Ad, E-mail və ya ID</span>
                  </div>
                  <div className="relative bg-zinc-900/60 rounded-xl border border-zinc-800/80 focus-within:border-indigo-500/80 focus-within:ring-2 focus-within:ring-indigo-900/20 transition-all overflow-hidden">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-zinc-500">
                      <User className="w-4 h-4" />
                    </div>
                    <input
                      id="auth_login_input"
                      type="text"
                      autoComplete="username"
                      required
                      value={username}
                      onChange={(e) => {
                        setErrorMsg(null);
                        setUsername(e.target.value);
                      }}
                      placeholder="Ad, e-mail prefiksi və ya admin"
                      className="w-full bg-transparent pl-10 pr-4 py-3 text-xs text-white placeholder-zinc-600 outline-none font-medium focus:bg-zinc-900/40"
                    />
                  </div>
                </div>

                {/* 2. Password secure input field */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label htmlFor="auth_password_input" className="text-[10px] font-mono font-bold text-zinc-500 uppercase tracking-wider">
                      Giriş şifrəsi
                    </label>
                    <span className="text-[9px] text-zinc-650 font-mono select-none">məxfi</span>
                  </div>
                  <div className="relative bg-zinc-900/60 rounded-xl border border-zinc-800/80 focus-within:border-indigo-500/80 focus-within:ring-2 focus-within:ring-indigo-900/20 transition-all overflow-hidden">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-zinc-500">
                      <Lock className="w-4 h-4" />
                    </div>
                    <input
                      id="auth_password_input"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => {
                        setErrorMsg(null);
                        setPassword(e.target.value);
                      }}
                      placeholder="Şifrənizi daxil edin"
                      className="w-full bg-transparent pl-10 pr-12 py-3 text-xs text-white placeholder-zinc-600 outline-none font-mono focus:bg-zinc-900/40"
                    />
                    <button
                      type="button"
                      id="auth_password_eye_toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-zinc-500 hover:text-indigo-400 transition-colors cursor-pointer select-none"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>

                  {/* Password security-meter */}
                  {password && (
                    <div className="pt-1.5 space-y-1" id="password_strength_assembly">
                      <div className="flex items-center justify-between text-[9px] font-mono">
                        <span className="text-zinc-500">Şifrə gücü:</span>
                        <span className="font-extrabold">{strength.label}</span>
                      </div>
                      <div className="h-[3px] w-full bg-zinc-850 rounded-full overflow-hidden">
                        <div className={`h-full transition-all duration-300 ${strength.color} ${strength.width}`} />
                      </div>
                    </div>
                  )}
                </div>

                {/* Error status panel */}
                {errorMsg && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-3 bg-rose-500/5 border border-rose-500/20 rounded-xl flex items-start gap-2 text-rose-400"
                    id="auth_error_banner"
                  >
                    <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />
                    <p className="text-[11px] leading-relaxed font-medium">
                      {errorMsg}
                    </p>
                  </motion.div>
                )}

                {/* Submit Sign-In button */}
                <button
                  type="submit"
                  id="login_submit_btn_element"
                  className="w-full bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white py-3 px-4 font-bold rounded-xl text-xs flex items-center justify-center gap-2 transition-all cursor-pointer focus:ring-2 focus:ring-indigo-500/30 shadow-lg shadow-indigo-600/10 select-none font-mono tracking-wider"
                >
                  <Key className="w-3.5 h-3.5 text-indigo-300" />
                  <span>SİSTEMƏ DAXİL OL</span>
                </button>

                {/* ADMIN BYPASS BUTTON */}
                <div className="pt-2 border-t border-zinc-900 flex flex-col gap-2" id="dev_bypass_container">
                  <button
                    type="button"
                    onClick={handleAdminBypass}
                    className="w-full py-2 px-4 border border-indigo-500/30 hover:border-indigo-500 bg-indigo-950/20 hover:bg-indigo-950/55 text-indigo-300 hover:text-indigo-200 rounded-xl text-[11px] font-bold flex items-center justify-center gap-2 transition-all cursor-pointer select-none font-mono tracking-wide"
                  >
                    <Sparkles className="w-3.5 h-3.5 text-indigo-400 animate-pulse" />
                    <span>ADMİN OLARAQ DAXİL OL (KƏNAR KEÇİD)</span>
                  </button>
                </div>
              </motion.form>
            )}
          </AnimatePresence>

          {/* Secure disclaimer footnote */}
          <div className="mt-8 pt-5 border-t border-zinc-900/80 text-center space-y-1">
            <p className="text-[10px] text-zinc-650 font-mono">
              SECURİTY GATEWAY IP: 224.16.51.90
            </p>
            <p className="text-[9px] text-zinc-500 leading-normal max-w-[280px] mx-auto select-none">
              İcazəsiz giriş cəhdləri avtomatik olaraq sistem tərəfindən qeydə alınır və bloklanır
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
